/**
 * ResultsVisualCard — the unified results-page hero.
 *
 * Renders the bitmap visualizer as a full-width image with a frosted-glass
 * overlay row (price + room config + slatwall config) flush to the bottom.
 * Below the card: stylized space selector, play/pause auto-cycle, currency
 * toggle.
 *
 * The transition type is configured via the admin Settings page (see
 * AppSettingsContext + src/config/transitions.ts) — no end-user picker.
 *
 * Used by both EstimateResults (live session) and SharedEstimate (shared
 * links). Data flows through `wizardState` so a single code path handles
 * all three calculation modes.
 */

import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutGrid,
  Layers,
  Play,
  Pause,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { EstimateOutput, WizardState, Currency, TotalFormat } from '../../types';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import { currencyPolicy } from '../../utils/currencyPolicy';
import { SpaceSelector } from './SpaceSelector';
import { SPACE_OPTIONS } from './resultsSpaceOptions';
import { SPACE_MAP, buildVisualizerPayload, buildVisualizerUrl } from './visualizerPayload';
import { useImageTransition } from './useImageTransition';
import { RenderPromptBanner } from './RenderPromptBanner';
import { RenderStage } from './render/RenderStage';
import type { FloorPlan } from '../../calculations/plan/floorPlan';
import { useAppSettings } from '../../context/AppSettingsContext';
import { DataPoint, buildRoomDataPoints, buildSlatWallDataPoints, hasSlatWall } from './configDataPoints';
import './ResultsVisualCard.css';

interface ResultsVisualCardProps {
  estimateResult: EstimateOutput;
  wizardState: WizardState | null;
  currency: Currency;
  onCurrencyChange: (c: Currency) => void;
  /** Change the key to re-fire the price pulse animation. */
  pulseKey: string | number;
  /** Estimated-total format. Only shared/submitted estimates pass the retailer's
   *  'precise' setting; the live wizard always uses 'rounded'. */
  totalFormat?: TotalFormat;
  /** ISO date the pricing snapshot was computed. When set (saved estimates),
   *  the disclaimer gains a "valid for N days from <ISO date>" line. */
  pricedAt?: string | null;
  /** Length of the pricing validity window in days (default 30). */
  validDays?: number;
  /** Whether the estimate has been saved (a designed room's banner changes from an invitation to a note). */
  saved?: boolean;
  /**
   * A saved estimate from the 3D designer: the stage polls its render job and
   * shows the photoreal pictures (and the 360) as they land, in place of the
   * quick still and its banner.
   */
  render?: { guid: string; plan: FloorPlan; focusRoomId: string | null };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function chunkDataPoints<T>(items: T[], size = 4): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function formatAmount(
  usd: number,
  cad: number,
  currency: Currency,
  totalFormat: TotalFormat,
): { symbol: string; value: string } {
  const amount = currency === 'USD' ? usd : cad;
  if (totalFormat === 'precise') {
    return {
      symbol: '$',
      value: amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    };
  }
  const rounded = Math.ceil(amount / 100) * 100;
  return {
    symbol: '$',
    value: rounded.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  };
}

// ── Sub-components ──────────────────────────────────────────────────────────

const OverlayConfigPanel: React.FC<{
  title: string;
  TitleIcon: LucideIcon;
  dataPoints: DataPoint[];
  className?: string;
}> = ({ title, TitleIcon, dataPoints, className }) => {
  const chunks = chunkDataPoints(dataPoints, 4);
  return (
    <div className={`overlay-config h-100 d-flex flex-column px-3 py-2 ${className || ''}`}>
      <div className="d-flex align-items-center gap-2 overlay-config-title">
        <TitleIcon size={15} className="overlay-config-title-icon" />
        <span className="fw-bold text-uppercase">{title}</span>
      </div>
      <div className="row g-2 flex-grow-1 mt-1">
        {chunks.map((chunk, i) => (
          <div key={i} className="col">
            <ul className="list-unstyled m-0 overlay-config-list">
              {chunk.map((dp, j) => (
                <li key={j} className="d-flex align-items-center gap-1">
                  <dp.Icon size={14} className="flex-shrink-0 overlay-config-icon" />
                  <div>
                    <strong>{dp.label}:</strong> <span>{dp.value}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const OverlayPricePanel: React.FC<{
  totals: EstimateOutput['totals'];
  currency: Currency;
  pulseKey: string | number;
  totalFormat: TotalFormat;
}> = ({ totals, currency, pulseKey, totalFormat }) => {
  const { t } = useTranslation('results');
  const { symbol, value } = formatAmount(totals.totalCostUSD, totals.totalCostCAD, currency, totalFormat);
  return (
    <div className="overlay-price h-100 d-flex flex-column justify-content-center align-items-center text-center px-3 py-2">
      <span className="overlay-price-label text-uppercase">{t('cost.estimatedTotal')}</span>
      {/* key remounts the span on pulse-key change, re-firing the animation */}
      <span key={pulseKey} className="rvc-total-value">
        {symbol}{value}
      </span>
      <span className="overlay-price-currency">{currency}</span>
    </div>
  );
};

/**
 * PixelBlockOverlay — canvas-based block wipe transition.
 *
 * Mounts only while animating with transition='pixelBlock'. Splits the card
 * into a 32×20 grid, shuffles the block order, and reveals the new image
 * block-by-block over the animation window.
 */
const PixelBlockOverlay: React.FC<{
  nextUrl: string | null;
  durationMs: number;
}> = ({ nextUrl, durationMs }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!nextUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = parent.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);
    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;

    const COLS = 32;
    const ROWS = 20;
    const blockW = width / COLS;
    const blockH = height / ROWS;
    const total = COLS * ROWS;
    const order: number[] = Array.from({ length: total }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }

    const img = new Image();
    img.src = nextUrl;
    let raf = 0;
    let startTime = 0;
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      startTime = performance.now();
      const tick = (now: number) => {
        if (cancelled) return;
        const progress = Math.min(1, (now - startTime) / durationMs);
        const toIdx = Math.floor(progress * total);
        for (let i = 0; i < toIdx; i++) {
          const blockIdx = order[i];
          const cx = (blockIdx % COLS) * blockW;
          const cy = Math.floor(blockIdx / COLS) * blockH;
          const sx = (cx / width) * img.naturalWidth;
          const sy = (cy / height) * img.naturalHeight;
          const sw = (blockW / width) * img.naturalWidth;
          const sh = (blockH / height) * img.naturalHeight;
          ctx.drawImage(img, sx, sy, sw, sh, cx, cy, blockW + 1, blockH + 1);
        }
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      ctx.clearRect(0, 0, width, height);
    };
  }, [nextUrl, durationMs]);

  return <canvas ref={canvasRef} className="rvc-pixel-canvas" />;
};

// ── Currency picker ─────────────────────────────────────────────────────────

const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'CAD', label: 'CAD (C$)' },
];

const CurrencyPicker: React.FC<{
  currency: Currency;
  onCurrencyChange: (c: Currency) => void;
}> = ({ currency, onCurrencyChange }) => {
  const { t } = useTranslation('results');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentLabel = CURRENCY_OPTIONS.find(o => o.value === currency)?.label ?? currency;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="rvc-currency-line d-inline-flex align-items-center" ref={wrapperRef}>
      <span className="rvc-currency-label me-2">{t('visual.priceDisplayedIn')}</span>
      <div className="rvc-currency-wrap position-relative">
        <button
          type="button"
          className="rvc-currency-pill"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          {currentLabel}
        </button>
        {open && (
          <ul className="rvc-currency-menu" role="listbox" aria-label={t('visual.displayCurrency')}>
            {CURRENCY_OPTIONS.map(opt => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === currency}
                className={`rvc-currency-option ${opt.value === currency ? 'is-selected' : ''}`}
                onClick={() => {
                  onCurrencyChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

export const ResultsVisualCard: React.FC<ResultsVisualCardProps> = ({
  estimateResult,
  wizardState,
  currency,
  onCurrencyChange,
  pulseKey,
  totalFormat = 'rounded',
  pricedAt = null,
  validDays = 30,
  saved = false,
  render,
}) => {
  const { t } = useTranslation('results');
  const { settings } = useAppSettings();
  const { partner } = usePartnerBranding();
  // Single-currency retailers lose the toggle; the label still states the currency.
  const { canToggle: canToggleCurrency } = currencyPolicy(partner?.currenciesSupported);
  const transitionType = settings.defaultTransition;
  const transitionDuration = settings.transitionDurationMs;
  const cycleIntervalMs = settings.cycleIntervalMs;

  // Initial space is always the user's saved spaceType (via SPACE_MAP), never a
  // dropdown default. The `selectedSpace` state is ephemeral — viewing
  // different rooms is never persisted back to wizardState.
  const initialSpace = useMemo(() => {
    if (!wizardState) return 'generic';
    return SPACE_MAP[wizardState.applicationConfig.spaceType] || 'generic';
  }, [wizardState]);

  const [selectedSpace, setSelectedSpace] = useState<string>(initialSpace);
  const [cycling, setCycling] = useState<boolean>(settings.autoPlayEnabled);
  // An estimate made in the 3D designer shows the user's own room — the still
  // taken on the way here — in place of the stock space pictures.
  const still = estimateResult.designer ? estimateResult.preview : undefined;

  // Reset selection if a new estimate arrives (parent remounts via pulseKey).
  useEffect(() => {
    setSelectedSpace(initialSpace);
  }, [initialSpace]);

  // Build the visualizer URL for the currently-selected space.
  const targetUrl = useMemo(() => {
    if (still) return still.image;
    const payload = buildVisualizerPayload(wizardState, estimateResult, selectedSpace);
    return buildVisualizerUrl(payload);
  }, [wizardState, estimateResult, selectedSpace, still]);

  // Lookahead: URL for the next item in the cycle (for preload).
  const lookaheadUrl = useMemo(() => {
    if (still) return null;
    const idx = SPACE_OPTIONS.findIndex(o => o.id === selectedSpace);
    if (idx === -1) return null;
    const next = SPACE_OPTIONS[(idx + 1) % SPACE_OPTIONS.length];
    const payload = buildVisualizerPayload(wizardState, estimateResult, next.id);
    return buildVisualizerUrl(payload);
  }, [selectedSpace, wizardState, estimateResult, still]);

  const { currentSrc, nextSrc, isAnimating, isFirstLoad } = useImageTransition({
    targetUrl,
    transition: transitionType,
    lookaheadUrl,
    durationMs: transitionDuration,
  });

  // Auto-cycle: advance through SPACE_OPTIONS on the admin-configured cadence.
  useEffect(() => {
    if (!cycling || still) return;
    const id = window.setInterval(() => {
      setSelectedSpace(prev => {
        const idx = SPACE_OPTIONS.findIndex(o => o.id === prev);
        const next = SPACE_OPTIONS[(idx + 1) % SPACE_OPTIONS.length];
        return next.id;
      });
    }, cycleIntervalMs);
    return () => window.clearInterval(id);
  }, [cycling, selectedSpace, cycleIntervalMs, still]);

  // Data points for the overlay columns.
  const roomPoints = useMemo(
    () => wizardState ? buildRoomDataPoints(wizardState, estimateResult) : [],
    [wizardState, estimateResult],
  );
  const slatPoints = useMemo(
    () => wizardState ? buildSlatWallDataPoints(wizardState) : [],
    [wizardState],
  );
  const slatShown = hasSlatWall(wizardState);

  // Flex-basis values (exact 25 / 37.5 / 37.5 split — overrides Bootstrap cols).
  const roomFlex = slatShown ? '0 0 37.5%' : '1 1 auto';
  const slatFlex = '0 0 37.5%';

  const handleCycleToggle = useCallback((play: boolean) => {
    setCycling(play);
  }, []);

  // The frosted strip's height, published on the card as --rvc-overlay-h so the
  // preview banner (and the render dock) sit just above it rather than under it.
  // On phones the strip is in flow (position static) and the banner sits at 0.
  const overlayRef = useRef<HTMLDivElement>(null);
  // The strip under the stage that takes the render's thumbnails on a phone (state, so the stage re-renders once it exists).
  const [renderStrip, setRenderStrip] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = overlayRef.current;
    const card = el?.parentElement;
    if (!el || !card) return undefined;
    const measure = () => {
      const absolute = window.getComputedStyle(el).position === 'absolute';
      card.style.setProperty('--rvc-overlay-h', `${absolute ? el.offsetHeight : 0}px`);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const overlayClassName = [
    'results-visual-overlay',
    isAnimating ? `rvc-animating rvc-transition-${transitionType}` : '',
  ].filter(Boolean).join(' ');

  const imgClassName = [
    'results-visual-img',
    isAnimating && transitionType === 'slide' ? 'rvc-slide-out' : '',
  ].filter(Boolean).join(' ');

  const nextImgClassName = [
    'results-visual-img-next',
    transitionType === 'slide' ? 'rvc-slide-in' : 'rvc-fade-in',
  ].filter(Boolean).join(' ');

  return (
    <div className="row g-0 mb-4">
      <div className="col-12">
        <div className="results-visual-card position-relative overflow-hidden rounded-4">
          {/* Fixed-size stage: holds 16:9 space regardless of image state */}
          <div className="rvc-image-stage">
            {render && still ? (
              <RenderStage guid={render.guid} plan={render.plan} focusRoomId={render.focusRoomId} preview={still} alt={t('visual.spaceAlt')} dockOutlet={renderStrip} />
            ) : (<>
            {isFirstLoad && (
              <div className="rvc-shimmer" />
            )}
            {currentSrc && (
              <img
                src={currentSrc}
                className={imgClassName}
                alt={t('visual.spaceAlt')}
                style={{ animationDuration: `${transitionDuration}ms` }}
              />
            )}

            {/* Transition layer */}
            {nextSrc && transitionType !== 'pixelBlock' && (
              <img
                src={nextSrc}
                className={nextImgClassName}
                alt=""
                style={{ animationDuration: `${transitionDuration}ms` }}
              />
            )}
            {nextSrc && transitionType === 'pixelBlock' && (
              <PixelBlockOverlay nextUrl={nextSrc} durationMs={transitionDuration} />
            )}
            {still && <RenderPromptBanner variant={saved ? 'queued' : 'prompt'} />}
            </>)}
          </div>

          {/* On a phone the render's pictures strip sits here, under the picture (RenderStage fills it). */}
          {render && still && <div className="rvc-render-strip" ref={setRenderStrip} />}

          {/* Frosted overlay row */}
          <div className={overlayClassName} ref={overlayRef}>
            <div className="d-flex h-100">
              <div className="overlay-col-price" style={{ flex: '0 0 25%' }}>
                <OverlayPricePanel
                  totals={estimateResult.totals}
                  currency={currency}
                  pulseKey={pulseKey}
                  totalFormat={totalFormat}
                />
              </div>
              <div className="overlay-col-room" style={{ flex: roomFlex }}>
                <OverlayConfigPanel
                  title={t('pdf.cards.room')}
                  TitleIcon={LayoutGrid}
                  dataPoints={roomPoints}
                />
              </div>
              {slatShown && (
                <div className="overlay-col-slat" style={{ flex: slatFlex }}>
                  <OverlayConfigPanel
                    title={t('pdf.cards.slatWall')}
                    TitleIcon={Layers}
                    dataPoints={slatPoints}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selector strip (not for a designed room: it shows the user's own room, nothing else) */}
        {!still && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-3 mb-2 flex-wrap">
          <span className="text-muted small fw-semibold">{t('visual.chooseSpace')}</span>
          <SpaceSelector
            options={SPACE_OPTIONS}
            value={selectedSpace}
            onChange={setSelectedSpace}
          />
          <div className="rvc-play-pause d-inline-flex align-items-center gap-1">
            <button
              type="button"
              className="rvc-play-pause-btn"
              data-active={cycling}
              onClick={() => handleCycleToggle(true)}
              aria-label={t('visual.play')}
              aria-pressed={cycling}
            >
              <Play size={16} />
            </button>
            <button
              type="button"
              className="rvc-play-pause-btn"
              data-active={!cycling}
              onClick={() => handleCycleToggle(false)}
              aria-label={t('visual.pause')}
              aria-pressed={!cycling}
            >
              <Pause size={16} />
            </button>
          </div>
        </div>
        )}

        {/* Currency toggle — minimal: text label + subtle gray pill that
            opens a small custom popover. The auto-detect logic in
            useCurrency still drives the initial value; this is just a
            quiet override. Custom popover (not native <select>) so we
            can fully control the styling — soft edges, gentle hover. */}
        <div className="d-flex justify-content-center mb-2">
          {canToggleCurrency ? (
            <CurrencyPicker currency={currency} onCurrencyChange={onCurrencyChange} />
          ) : (
            <div className="rvc-currency-line d-inline-flex align-items-center">
              <span className="rvc-currency-label me-2">{t('visual.priceDisplayedIn')}</span>
              <span className="fw-semibold">
                {CURRENCY_OPTIONS.find(o => o.value === currency)?.label ?? currency}
              </span>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="rvc-disclaimer text-center text-muted">
          <Trans i18nKey="disclaimer.short" ns="results" components={{ b: <strong /> }} />
          {pricedAt && (() => {
            const d = new Date(pricedAt);
            if (Number.isNaN(d.getTime())) return null;
            const iso = d.toISOString().slice(0, 10); // YYYY-MM-DD
            return (
              <>
                {' '}<Trans i18nKey="disclaimer.validity" ns="results"
                  count={validDays} values={{ count: validDays, date: iso }}
                  components={{ b: <strong /> }} />
              </>
            );
          })()}
        </p>
      </div>
    </div>
  );
};
